/**
 * Regulatory Export service — wraps `/programs/{programId}/ae-export/*`.
 * Uses metadata + short-lived signed URL delivery.
 */
import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type { AeExportRequestDto, AeExportResultDto } from '../types/pharmacovigilance';

const base = (programId: string) => `/programs/${programId}/ae-export`;

export const aeRegulatoryExportService = {
  /**
   * Generates a regulatory export and triggers browser download via signed URL.
   */
  async generateAndDownload(programId: string, request: AeExportRequestDto): Promise<{ fileName: string; sizeBytes: number }> {
    const res = await apiClient.post<ApiResponse<AeExportResultDto>>(`${base(programId)}/generate`, request);
    const meta = res.data.data;

    if (!meta) {
      throw new Error('Export metadata is missing.');
    }

    if (!meta.downloadUrl) {
      throw new Error('Secure download link is unavailable. Please regenerate the export.');
    }

    const a = document.createElement('a');
    a.href = meta.downloadUrl;
    a.download = meta.fileName;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();

    return { fileName: meta.fileName, sizeBytes: meta.fileSizeBytes };
  },

  /**
   * Generates an export and returns metadata only (no download).
   */
  async preview(programId: string, request: AeExportRequestDto): Promise<AeExportResultDto> {
    const res = await apiClient.post<ApiResponse<AeExportResultDto>>(`${base(programId)}/preview`, request);
    return res.data.data!;
  },
};
